<template>
  <div class="content-categories-table">
    <div class="vx-row md:mb-6">
      <div class="vx-col w-full md:flex md:justify-between">
        <!-- Start Filtration -->
        <div class="md:col-6 sm:col-12">
          <span class="inline-block categories-hint">
            Select an adset from the Campaign Insights section to enable editing of the targeted categories.
          </span>
        </div>
        <div class="md:col-6 sm:col-12">
          <ul class="md:text-right mb-10">
            <li class="inline-block md:float-right parent-li">
              <vs-button
                id="insights-table-content-categories-export-button"
                class="speakol-table-export-categories-button sm:w-full"
                @click="isExportPopupActive = true"
                :disabled="isExportButtonDisabled || !visibleColumns.length""
              >
                {{ $t('Export') }}
              </vs-button>
            </li>
          </ul>
        </div>
        <!-- End Filtration -->
      </div>
    </div>

    <SpeakolExport
      :fileName="'ContentCategories'"
      :isActive="isExportPopupActive"
      exportType="adset-content-categories"
      :params="exportParams"
      @hide="isExportPopupActive = false"
      :columns="visibleColumns"
    />

    <a-g-speakol-table
      ref="contentCategoryTable"
      :columns="agColumns"
      :filters="agFilters"
      :options="agOptions"
      @sortChanged="sortChanged"
      @dataChange="isExportButtonDisabled = $event.totalItems === 0"
      @visibleColumnsChanged="updateVisibleColumns"
    ></a-g-speakol-table>

    <speakol-response-popup
      v-on:hide-response-popup="stateModal.show = false"
      :responsePopupData="stateModal"
    />
  </div>
</template>

<script>
import AGSpeakolTable from '@/components/AGSpeakolTable'
import { Server, DataSource } from '@/datasources/DataSource'
import SpeakolExport from '@/components/SpeakolExport'
import ToggleContentCategoryStatus from '@/views/campaigns/insights/components/_content_partials/_tables_partials/_ag_custom_cells/ToggleContentCategoryStatus.vue'
import AsetContentCategoryBidColumn from '@/views/campaigns/insights/components/_content_partials/_tables_partials/_ag_custom_cells/AsetContentCategoryBidColumn.vue'

export default {
  components: {
    ToggleContentCategoryStatus,
    AsetContentCategoryBidColumn,
    AGSpeakolTable,
    SpeakolExport
  },
  props: {
    maxBidOfSelectedAdset: {
      type: Number,
      required: true
    },
    canEditBid: {
      type: Boolean,
      required: true
    },
    pixelEvent: {
      type: String,
      default: 'conversions',
      required: false
    },
    filters: {
      type: Object,
      required: true
    },
    campaignType: {
      type: String,
      required: true
    },
    campaignAdsetsBackup: null
  },
  computed: {
    exportParams () {
      this.getAgFilters()
      return {
        ...this.getAgFilters(),
        sort: [this.exportSortModel]
      }
    },
    filterationConversionName () {
      return (!this.pixelEvent || this.pixelEvent === 'total') ? 'conversions' : this.pixelEvent
    }
  },
  data () {
    return {
      visibleColumns: [],
      isExportButtonDisabled: false,
      isExportPopupActive: false,
      exportSortModel: {
        colId: 'impressions',
        sort:  'desc'
      },
      agFilters: this.getAgFilters(),
      agOptions: {
        context: {
          parent: this
        },
        dataSource: DataSource(
          Server(`/campaigns/${this.$route.params.id}/insights/content-categories`)
        )
      },
      agColumns: this.getAgColumns(),
      stateModal: {
        type: 'error',
        show: false,
        body: 'Error message'
      }
    }
  },
  mounted () {
    const self = this
    this.$on('modal-updated', function (modalContent) {
      self.stateModal = modalContent
    })
  },
  methods: {
     updateVisibleColumns(visibleColumns) {
      this.visibleColumns = visibleColumns.map(col => col.colId)
     },
    sortChanged (sortModel) {
      const sortColumn = sortModel[0] || {}

      this.exportSortModel = {
        colId: sortColumn.colId || 'impressions',
        sort: sortColumn.sort || 'desc'
      }
    },
    getAgColumns () {
      const self = this

      const columns = [
        {
          headerName: 'Category Name',
          field: 'category_name',
          minWidth: 200,
          pinned: true
        },
        {
          headerName: 'Impressions',
          minWidth: 162,
          field: 'impressions',
          sortable: true,
          valueGetter (params) {
            return self.$options.filters.addComma(params.data.impressions)
          }
        },
        {
          headerName: 'Clicks',
          field: 'clicks',
          sortable: true,
          minWidth: 110,
          valueGetter (params) {
            return self.$options.filters.addComma(params.data.clicks)
          }
        },
        {
          headerName: 'CTR',
          field: 'ctr',
          sign: '%',
          minWidth: 95,
          sortable: true,
          valueGetter (params) {
            return self.$options.filters.addComma(params.data.ctr, '%')
          }
        },
        {
          headerName: 'Avg. CPC',
          field: 'average_cpc',
          sign: '$',
          sortable: true,
          minWidth: 135,
          valueGetter (params) {
            return self.$options.filters.addComma(params.data.average_cpc, '$')
          }
        }
      ]

      if (this.campaignType === 'Video' || this.campaignType === 'Stories') {
        const videoCols = ['video_views', 'video_starts', 'video_first_quarters', 'video_midpoints', 'video_third_quarters', 'video_completes', 'video_repeats']

        videoCols.forEach(col => {
          columns.push({
            headerName: this.$t(col),
            field: col,
            minWidth: 220,
            sortable: true,
            valueGetter (params) {
              return self.$options.filters.addComma((params.data || {})[col] || 0)
            }
          })
        })
      }

      const pixelEventHeader = this.$t(
        !this.pixelEvent ? 'Conversions' : this.pixelEvent
      )

      columns.push(
        ...[
          {
            headerName: pixelEventHeader,
            field: self.filterationConversionName || 'conversions',
            sortable: true,
            minWidth: 200,
            valueGetter (params) {
              return self.$options.filters.addComma(
                (params.data || [])[self.filterationConversionName]
              )
            }
          }
        ]
      )

      columns.push(
        ...[
          {
            headerName: 'CPA',
            field: 'filter_cpa',
            sortable: true,
            sign: '$',
            minWidth: 155,
            valueGetter (params) {
              return self.$options.filters.addComma(params.data.filter_cpa, '$')
            }
          }
        ]
      )

      if (this.filters.adset) {
        columns.unshift({
          headerName: this.$t(''),
          field: 'change-status',
          pinned: true,
          minWidth: 78,
          maxWidth: 78,
          resizable: false,
          cellRendererFramework: 'ToggleContentCategoryStatus'
        })

        columns.push({
          headerName: this.$t('Bid'),
          headerTooltip: this.$t('Bid'),
          field: 'bid',
          sortable: true,
          minWidth: 200,
          cellRendererFramework: 'AsetContentCategoryBidColumn'
        })
      }

      columns.push({
        headerName: 'Spent',
        field: 'spent',
        sign: '$',
        minWidth: 110,
        sortable: true,
        resizable: false,
        valueGetter (params) {
          return self.$options.filters.addComma(params.data.spent, '$')
        }
      })

      return columns
    },
    getAgFilters () {
      return {
        ...this.filters,
        ...{
          pixelEvent: (!this.pixelEvent || this.pixelEvent === 'total') ? 'conversions' : this.pixelEvent,
          campaignId: this.$route.params.id        }
      }
    },
    applyFilters () {
      this.agFilters.adset = this.filters.adset || null
      this.agFilters.pixelEvent = this.filterationConversionName
      this.agFilters.dateRange = this.filters.dateRange

      // update columns
      this.agColumns = this.getAgColumns()

      if (this.$refs['contentCategoryTable']) {
        this.$refs['contentCategoryTable'].setColumnDefs([])
        this.$refs['contentCategoryTable'].setColumnDefs(this.agColumns)

        this.$refs['contentCategoryTable'].applyFilters()
      }
    }
  }
}
</script>

<style lang='scss'>
.content-categories-table {
  .categories-hint {
    padding-top: 29px;
    font-weight: normal;
    font-size: 14px;
    color: #0092d7;
  }

  .speakol-table-export-categories-button {
    min-width: 135px;
    border-radius: 4px;
    padding: 0.57rem 2rem !important;
    background-color: white !important;
    color: #0092d7 !important;
    border: 1px solid #0092d7;
  }
}

@media (max-width: 768px) {
  .speakol-table-export-categories-button {
    width: 100% !important;
  }

  .parent-li {
    width: 100%;
  }

  .categories-hint {
    margin-bottom: 10px;
  }
}
</style>
